import {computed, getCurrentInstance, ref, onMounted} from 'vue';
import useVuelidate from '@vuelidate/core';
import {email, helpers, required} from '@vuelidate/validators';
import {phone} from '../modules/regitValidators';
import axiosClient from '../modules/axiosClient';

type UserObj = {
    id: number,
    firstName: string|null,
    lastName: string|null,
    email: string|null,
    phone: string|null,
}

export default function(user: UserObj) {
    const instance = getCurrentInstance();
    const modal = instance?.appContext.config.globalProperties.$modal;

    const state = {
        conciergeFirstName: ref(user.firstName || ''),
        conciergeLastName: ref(user.lastName || ''),
        conciergeEmail: ref(user.email || ''),
        conciergePhone: ref(user.phone || ''),
        conciergeReason: ref(''),
        conciergeReasonOptions: [
            {value: '', text: 'Select reason'},
            {value: 'buying', text: 'Buying'},
            {value: 'selling', text: 'Selling'},
            {value: 'changing', text: 'Changing'},
        ],
        conciergeTime: ref(''),
        conciergeTimeOptions: [
            {value: '', text: 'Select time'},
            {value: 'morning', text: 'Morning'},
            {value: 'afternoon', text: 'Afternoon'},
            {value: 'evening', text: 'After 5pm'},
        ],
        conciergeSubmitted: ref(false),
        conciergeError: ref(false),
    };

    const rules = {
        conciergeFirstName: {
            required: helpers.withMessage('Please enter your name', required),
        },
        conciergeLastName: {
            required: helpers.withMessage('Please enter your surname', required),
        },
        conciergeEmail: {
            required: helpers.withMessage('Please enter your email', required),
            email,
        },
        conciergePhone: {
            required: helpers.withMessage('Please enter your phone', required),
            phone: helpers.withMessage('Value is not a valid phone number', phone),
        },
        conciergeReason: {
            required: helpers.withMessage('Please select a reason', required),
        },
        conciergeTime: {
            required: helpers.withMessage('Please choose a time', required),
        },
    };

    const v = useVuelidate(rules, state);

    function openConciergeCallModal() {
        modal?.show('modal-concierge-call');
    }

    async function conciergeCallSubmit() {
        const validationPassed = await v.value.$validate();
        if (validationPassed) {
            const payload = {
                'firstname': state.conciergeFirstName.value,
                'surname': state.conciergeLastName.value,
                'tel': state.conciergePhone.value,
                'email': state.conciergeEmail.value,
                'reason': state.conciergeReason.value,
                'time': state.conciergeTime.value,
            };

            axiosClient.post('/auto-expert', payload).then((response) => {
                if (response.status === 201) {
                    state.conciergeSubmitted.value = true;
                    window.sendAnalyticsEvent('Conversion Tracking', 'Lead Generation', 'Lead - callback');
                } else {
                    state.conciergeError.value = true;
                }
            }, (response) => {
                state.conciergeError.value = true;
                console.log('API call error: ', response);
            });
        }
    }

    const availableConciergeReasons = state
        .conciergeReasonOptions
        .filter((item) => item.value !== '')
        .map((item) => item.value);

    const hideConciergeReason = computed((): boolean => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryIntent = urlParams.get('intent') || '';

        return availableConciergeReasons.includes(queryIntent);
    });

    onMounted(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryIntent = urlParams.get('intent') || '';

        if (availableConciergeReasons.includes(queryIntent)) {
            state.conciergeReason.value = queryIntent;
        }
    });

    return {
        conciergeFirstName: state.conciergeFirstName,
        conciergeLastName: state.conciergeLastName,
        conciergeEmail: state.conciergeEmail,
        conciergePhone: state.conciergePhone,
        conciergeReason: state.conciergeReason,
        conciergeReasonOptions: state.conciergeReasonOptions,
        conciergeTime: state.conciergeTime,
        conciergeTimeOptions: state.conciergeTimeOptions,
        conciergeSubmitted: state.conciergeSubmitted,
        conciergeError: state.conciergeError,

        hideConciergeReason,

        conciergeValidator$: v,

        openConciergeCallModal,
        conciergeCallSubmit,
    };
}
